import PortalIdParser from 'PortalIdParser';
import {
  BYTES_IN_MEGABYTE,
  FILESIZE_UNITS,
  FileSizeUnit,
  FileType,
  FileTypes,
} from '../constants';
import { TypeToExtensions } from '../constants/fileTypes';
import FMFile, { ImageFileMeta } from '../types/FMFile';
import { FileSize } from '../types';
import { FileMetaAccessTypes } from '../constants/internal/FileMetaAccess';
import {
  FileMetaOrAccess,
  isChirpFileMeta,
  isFMFileMeta,
} from '../types/internal';
import { FileMeta } from '../../__generated__/chirp/com/hubspot/filemanager/rpc/FileMetaFetchRpc';

export function splitNameAndExtension(fileName: string): {
  name: string;
  extension: string;
} {
  const parts = fileName.split('.');

  if (parts.length === 1) {
    return {
      extension: '',
      name: fileName,
    };
  }

  return {
    extension: parts.pop() || '',
    name: parts.join('.'),
  };
}

export function isShutterstockImage(file: FMFile | FileMeta): boolean {
  const isFileMeta = isChirpFileMeta(file);
  const type = isFileMeta ? file.fileType : file.type;
  if (type !== FileTypes.IMG) {
    return false;
  }
  return isFileMeta
    ? Boolean(file.extendedMetadata?.thirdPartyIntegrations?.shutterstock_id)
    : Boolean((file.meta as ImageFileMeta).shutterstock_id);
}

const FORM_UPLOADS_FOLDER_NAME = 'form-uploads';

const getIsFileSubmittedThroughForms = (url: FMFile['url']): boolean => {
  const fileUrl = url || '';
  return (
    fileUrl.indexOf(
      `hubfs/${PortalIdParser.get()}/${FORM_UPLOADS_FOLDER_NAME}`
    ) > -1
  );
};

const getIsFileAccessibleAnonymously = (
  accessOrMeta: FileMetaOrAccess
): boolean => {
  if (isFMFileMeta(accessOrMeta)) {
    return accessOrMeta.allows_anonymous_access;
  }
  return (
    accessOrMeta === FileMetaAccessTypes.PUBLIC_INDEXABLE ||
    accessOrMeta === FileMetaAccessTypes.PUBLIC
  );
};

export const getIsFilePrivate = (
  accessOrMeta: FMFile['meta'] | FileMeta['access'],
  url: FMFile['url']
) => {
  return (
    !getIsFileAccessibleAnonymously(accessOrMeta) &&
    !getIsFileSubmittedThroughForms(url)
  );
};

export const getIsFileSensitive = (accessOrMeta: FileMetaOrAccess): boolean => {
  if (isFMFileMeta(accessOrMeta)) {
    return accessOrMeta.sensitive;
  }

  return accessOrMeta === FileMetaAccessTypes.SENSITIVE;
};

export const getIsFileExternal = (
  accessOrMeta: FileMetaOrAccess,
  url: FMFile['url']
): boolean => {
  return (
    !getIsFileAccessibleAnonymously(accessOrMeta) &&
    getIsFileSubmittedThroughForms(url)
  );
};

export const getType = (extensionOrType: string): FileType => {
  if (Object.prototype.hasOwnProperty.call(FileTypes, extensionOrType)) {
    return extensionOrType as FileType;
  }
  const type = Object.keys(TypeToExtensions).find((key) =>
    TypeToExtensions[key].includes(extensionOrType)
  );
  if (type) {
    return type as FileType;
  }
  return FileTypes.OTHER;
};

export const getFileType = (
  type: FMFile['type'] | FileMeta['fileType'],
  extension: FMFile['extension']
): FileType => {
  if (type && Object.values(FileTypes).includes(type as FileType)) {
    return type as FileType;
  }
  return getType(extension);
};

export const getIsImage = (
  type: FMFile['type'] | FileMeta['fileType'],
  extension: FMFile['extension']
) => {
  return getFileType(type, extension) === FileTypes.IMG;
};

export const getIsSvg = (
  type: FMFile['type'] | FileMeta['fileType'],
  extension: FMFile['extension'],
  encoding: FMFile['encoding'] | FileMeta['encoding']
) => {
  return getIsImage(type, extension) && encoding === 'svg';
};

export const getIsSvgTooLarge = (size: FMFile['size']) => {
  return size >= BYTES_IN_MEGABYTE;
};

export const getIsTemporaryUploadingFile = (id: FMFile['id']) => {
  return id <= 0;
};

export const splitFileIntoChunks = (file: File, chunkSize: number) => {
  const fileSize = file.size;
  const chunks = Math.ceil(fileSize / chunkSize);
  const fileChunks: Blob[] = [];

  for (let i = 0; i < chunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, fileSize);
    const chunk = file.slice(start, end);
    fileChunks.push(chunk);
  }

  return fileChunks;
};

export const getFileSizeInBytes = (fileSize: FileSize) => {
  const sizeUnits = Object.keys(FILESIZE_UNITS);
  const sizeRegex = new RegExp(
    `^(\\d+\\.?\\d*)\\s*(${sizeUnits.join('|')})$`,
    'i'
  );

  // if the number or unit don't match our expected format, throw an error
  const match = fileSize.match(sizeRegex);
  if (!match?.[1] || !match[2]) {
    console.error(
      '[getFileSizeInBytes] - fileSize does not match expected format'
    );
    return;
  }

  const sizeValue = parseFloat(match[1]);
  const sizeUnit = match[2].toUpperCase() as FileSizeUnit;
  const sizeInBytes = sizeValue * Math.pow(1024, sizeUnits.indexOf(sizeUnit));

  return Math.floor(sizeInBytes);
};

export function base64ToFile(base64String: string, filename: string) {
  const arr = base64String.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
